import { ChartConfig, ChartContainer, ChartTooltip } from "design-system/molecules/Chart"
import { useTheme } from "design-system/theme"
import { cn } from "design-system/utils"
import { isMobileDevice } from "lopermut"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import {
  getFulfillmentColor,
  getQuestionSolveRateColor,
  getSentimentColor,
  getSolveRateColor,
  getUrgencyColor,
} from "modules/shared/lib/sentiment"
import React, { useMemo } from "react"
import { Treemap, TreemapNode } from "recharts"
import {
  CanonicalTopicCompressionDataSchema,
  CanonicalTopicsCompressionDataSchema,
  UNCATEGORIZED_CANONICAL_TOPIC_ID,
} from "schema"
import z from "zod"
import { ColorByEnum } from "modules/dashboard/lib/colorBy"

const TEXT_PADDING = 10
const CELL_PADDING = 3
const FONT_SIZE = 12
const FONT_WEIGHT = 500

type ColorBy = keyof typeof ColorByEnum

const chartConfig = {} satisfies ChartConfig

export type NodeData = Omit<z.infer<typeof CanonicalTopicCompressionDataSchema>, "id"> & {
  id: number
  name?: string
  rank: number
  segment?: string
  isFiller?: boolean
  rings?: 1 | 2 | 3
}

export type NodePayload = {
  id?: number
  snapshotIds?: number[]
  segment?: string
}

type Props = {
  key: string
  data: z.infer<typeof CanonicalTopicsCompressionDataSchema> | undefined
  segment?: string
  noData?: boolean
  colorBy?: ColorBy
  presenceScore?: number
  hoveredLabel?: string | null
  onHoverLabelChange?: (label: string | null) => void
  onNodeClick?: (node: NodePayload) => void
  onTooltipChange?: (
    data: null | {
      node: NodeData
      coordinate: { x: number; y: number }
      containerRect: DOMRect | undefined
      segment?: string
    },
  ) => void
}

export const TreemapChart = ({
  key,
  data,
  noData,
  segment,
  presenceScore,
  colorBy,
  hoveredLabel,
  onHoverLabelChange,
  onNodeClick,
  onTooltipChange,
}: Props) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const lastTooltipRef = React.useRef<{
    nodeId: number | undefined
    nodeName: string | undefined
    segmentId: string | undefined
  } | null>(null)

  // Mobile touch handling state
  const [lastTappedNode, setLastTappedNode] = React.useState<number | null>(null)
  const isMobile = React.useMemo(() => isMobileDevice(), [])
  const chartData = useMemo(() => {
    const items = data?.map((topic, index) => ({
      rank: index + 1,
      id: topic.id,
      segment,
      name: formatCanonicalTopicLabel(topic.label),
      description: topic.description,
      totalItems: topic.totalItems,
      customersCount: topic.customersCount,
      conversationsCount: topic.conversationsCount,
      avgSentiment: topic.avgSentiment,
      avgSolveRate: topic.avgSolveRate,
      snapshotLabels: topic.snapshotLabels,
      snapshotIds: topic.snapshotIds,
      snapshotsCount: topic.snapshotIds?.length ?? 0,
      streakInWeeks: topic.streakInWeeks,
      avgPotentialSolveRate: topic.avgPotentialSolveRate,
      isNewTopic: topic.isNewTopic,
      colorBy,
    }))

    if (!items?.length) return items

    // Compute quantiles over snapshot counts to determine rings per node
    const sortedCounts = [...items.map((it) => it.snapshotsCount)].sort((a, b) => a - b)
    const q70 = sortedCounts.length ? sortedCounts[Math.floor((sortedCounts.length - 1) * 0.7)] : 0
    const q90 = sortedCounts.length ? sortedCounts[Math.floor((sortedCounts.length - 1) * 0.9)] : 0

    const ringsForCount = (count: number): 1 | 2 | 3 => {
      let rings: 1 | 2 | 3 = 1
      if (q90 && count >= q90) rings = 3
      else if (q70 && count >= q70) rings = 2
      return rings
    }

    return items.map((it) => ({ ...it, rings: ringsForCount(it.snapshotsCount) }))
  }, [data, segment, colorBy])

  return (
    <ChartContainer
      key={`${key}-container`}
      ref={containerRef}
      config={chartConfig}
      className={cn(
        "mx-auto h-full min-h-[280px] w-full",
        noData && "pointer-events-none grayscale opacity-20 dark:opacity-5",
      )}
      onMouseLeave={() => onHoverLabelChange?.(null)}
    >
      <Treemap
        data={chartData}
        dataKey="totalItems"
        nameKey="name"
        content={
          <CustomizedContent
            key={`${key}-content`}
            onNodeClick={onNodeClick}
            colorBy={colorBy}
            presenceScore={presenceScore}
            hoveredLabel={hoveredLabel}
            onHoverLabelChange={onHoverLabelChange}
            isMobile={isMobile}
            lastTappedNode={lastTappedNode}
            onLastTappedNodeChange={setLastTappedNode}
          />
        }
        isAnimationActive={false}
      >
        {/* Hover card */}
        <ChartTooltip
          allowEscapeViewBox={{ x: true, y: true }}
          content={({ active, payload, coordinate }) => {
            // Forward tooltip data upstream and render nothing here
            if (!active || !payload?.length) {
              // Only call onTooltipChange(null) if we were previously showing a tooltip
              if (lastTooltipRef.current !== null) {
                lastTooltipRef.current = null
                onTooltipChange?.(null)
              }
              return null
            }

            const node = (payload[0]?.payload ?? {}) as NodeData

            // Check if this is the same tooltip data as before
            const currentTooltipKey = {
              nodeId: node.id,
              nodeName: node.name,
              segmentId: segment,
            }

            const lastTooltip = lastTooltipRef.current
            const isSameTooltip =
              lastTooltip &&
              lastTooltip.nodeId === currentTooltipKey.nodeId &&
              lastTooltip.nodeName === currentTooltipKey.nodeName &&
              lastTooltip.segmentId === currentTooltipKey.segmentId

            // Only call callbacks if the tooltip content has actually changed
            if (!isSameTooltip) {
              lastTooltipRef.current = currentTooltipKey

              // Recharts 3 Treemap payloads carry no layout; coordinate is the node centre
              const x = coordinate?.x ?? 0
              const y = coordinate?.y ?? 0
              const containerRect = containerRef.current?.getBoundingClientRect()

              onTooltipChange?.({
                node,
                coordinate: { x, y },
                containerRect,
                segment,
              })
              onHoverLabelChange?.(node.name ?? null)
            }

            return null
          }}
        />
      </Treemap>
    </ChartContainer>
  )
}

// Customized content for each treemap cell
// Renders a white background rectangle with a colored overlay whose opacity
// decreases for every subsequent node (based on its index).
const CustomizedContent: React.FC<
  Partial<TreemapNode> &
    NodePayload & {
      key: string
      onNodeClick?: (node: NodePayload) => void
      colorBy?: ColorBy
      presenceScore?: number
      hoveredLabel?: string | null
      onHoverLabelChange?: (label: string | null) => void
      isMobile?: boolean
      lastTappedNode?: number | null
      onLastTappedNodeChange?: (nodeId: number | null) => void
    }
> = (props) => {
  const [isHovered, setIsHovered] = React.useState(false)
  const { theme } = useTheme()
  const { x = 0, y = 0, width = 0, height = 0, name } = props
  const depth = (props as { depth?: number }).depth
  if (depth === 0) {
    return null
  }

  // Filler nodes are used to equalize total sums across sibling treemaps.
  // They should not render any visual content.
  const node = props as unknown as {
    isFiller?: boolean
    avgSentiment?: number | null
    avgSolveRate?: number | null
    snapshotsCount: number
    colorBy?: ColorBy
    payload?: NodeData
    rings?: 1 | 2 | 3
    avgPotentialSolveRate?: number | null
    streakInWeeks?: number
    isNewTopic?: boolean
  }
  const isFiller = node.isFiller ?? node.payload?.isFiller
  if (isFiller) {
    return null
  }

  let color = "var(--neutral)"

  const nameStr = String(name ?? "")
  const topicId = String(node.payload?.id ?? props.id ?? "")
  const isSyntheticUncategorized = topicId === UNCATEGORIZED_CANONICAL_TOPIC_ID || topicId === "-1"
  const shouldEnableHover = !isSyntheticUncategorized

  if (isSyntheticUncategorized) {
    color = "var(--testing-2)"
  } else if (nameStr === "New Topic") {
    color = "var(--warning)"
  } else if (node.colorBy === ColorByEnum.Presence) {
    const rings = node.rings ?? node.payload?.rings ?? 1
    if (rings === 3) color = "var(--chart-primary)"
    else if (rings === 2) color = "var(--chart-secondary)"
    else color = "var(--neutral)"
  } else if (node.colorBy === ColorByEnum.SolveRate) {
    const avgSolveRate = node.avgSolveRate ?? node.payload?.avgSolveRate
    const solveRateColor = getSolveRateColor(Number(avgSolveRate))
    color = `var(--${solveRateColor})`
  } else if (node.colorBy === ColorByEnum.QuestionSolveRate) {
    const avgSolveRate = node.avgSolveRate ?? node.payload?.avgSolveRate
    const solveRateColor = getQuestionSolveRateColor(Number(avgSolveRate))
    color = `var(--${solveRateColor})`
  } else if (node.colorBy === ColorByEnum.Urgency) {
    const avgSolveRate = node.avgSolveRate ?? node.payload?.avgSolveRate
    const avgPotentialSolveRate = node.avgPotentialSolveRate ?? node.payload?.avgPotentialSolveRate
    color = `var(--${getUrgencyColor(Number(avgSolveRate ?? 0), Number(avgPotentialSolveRate ?? avgSolveRate ?? 0))})`
  } else if (node.colorBy === ColorByEnum.Fulfillment) {
    const avgSolveRate = node.avgSolveRate ?? node.payload?.avgSolveRate
    color = `var(--${getFulfillmentColor(Number(avgSolveRate))})`
  } else if (node.colorBy === ColorByEnum.Sentiment) {
    const avgSentiment = node.avgSentiment ?? node.payload?.avgSentiment
    const sentimentColor = getSentimentColor(Number(avgSentiment))
    color = `var(--${sentimentColor})`
  }

  const innerWidth = Math.max(width - CELL_PADDING * 2, 0)
  const innerHeight = Math.max(height - CELL_PADDING * 2, 0)

  const computeRadius = (w: number, h: number) => {
    const minSide = Math.min(w, h)
    const dynamic = minSide / 4
    const clamped = Math.max(1, Math.min(4, dynamic))
    return clamped
  }

  const innerRadius = computeRadius(innerWidth, innerHeight)

  // Measurement helpers
  const fontString = `${FONT_WEIGHT} ${FONT_SIZE}px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif`
  let canvasContext: CanvasRenderingContext2D | null = null
  const getContext = () => {
    if (typeof document === "undefined") return null
    if (canvasContext) return canvasContext
    const canvas = document.createElement("canvas")
    canvasContext = canvas.getContext("2d")
    return canvasContext
  }
  const measureTextWidth = (text: string) => {
    const ctx = getContext()
    if (!ctx) return Number.MAX_SAFE_INTEGER
    ctx.font = fontString
    return ctx.measureText(text).width
  }

  const contentWidth = Math.max(width - (TEXT_PADDING + CELL_PADDING) * 2, 0)
  const contentHeight = Math.max(height - (TEXT_PADDING + CELL_PADDING) * 2, 0)
  const firstWord = nameStr.split(/\s+/)[0] ?? ""
  const firstWordFitsHorizontally =
    contentWidth >= 1 &&
    contentHeight >= FONT_SIZE &&
    measureTextWidth(`${firstWord}\u2026`) <= contentWidth

  // For vertical text, approximate required height as number of non-space characters * line height (FONT_SIZE with leading-none)
  const nonSpaceCharCount = nameStr.replace(/\s+/g, "").length
  const verticalFits =
    contentWidth >= FONT_SIZE && // needs at least 1em column width
    contentHeight >= nonSpaceCharCount * FONT_SIZE

  const isLabelHovered =
    shouldEnableHover &&
    (isHovered || (props.hoveredLabel != null && props.hoveredLabel === nameStr))

  const opacityMap = {
    dark: {
      base: 0.25,
      hovered: 0.5,
    },
    light: {
      base: 0.25,
      hovered: 0.5,
    },
  }

  let opacity =
    theme === "dark"
      ? opacityMap["dark"][isLabelHovered ? "hovered" : "base"]
      : opacityMap["light"][isLabelHovered ? "hovered" : "base"]

  if (isSyntheticUncategorized) {
    opacity = isLabelHovered ? 0.22 : 0.12
  }

  if (node.colorBy === ColorByEnum.QuestionSolveRate) {
    const avgSolveRate = Number(node.avgSolveRate ?? node.payload?.avgSolveRate ?? 0)
    if (avgSolveRate >= 0.2 && avgSolveRate < 0.5) {
      opacity *= 0.6
    }
  }

  const handleClick = () => {
    if (!props.onNodeClick || isSyntheticUncategorized) return

    const nodeId = props.id

    if (props.isMobile && nodeId) {
      if (props.lastTappedNode === nodeId) {
        props.onNodeClick({ id: nodeId, snapshotIds: props.snapshotIds, segment: props.segment })
        props.onLastTappedNodeChange?.(null)
      } else {
        props.onLastTappedNodeChange?.(nodeId)
        props.onHoverLabelChange?.(name || null)
      }
    } else {
      props.onNodeClick({ id: nodeId, snapshotIds: props.snapshotIds, segment: props.segment })
    }
  }

  return (
    <g
      onMouseEnter={() => {
        if (!shouldEnableHover) return
        setIsHovered(true)
        props.onHoverLabelChange?.(nameStr || null)
      }}
      onMouseLeave={() => {
        if (!shouldEnableHover) return
        setIsHovered(false)
        props.onHoverLabelChange?.(null)
      }}
      onClick={handleClick}
      style={{ cursor: props.onNodeClick && !isSyntheticUncategorized ? "pointer" : undefined }}
      key={`${props.key}-${nameStr}`}
    >
      {/* Colored overlay inset to create spacing between cells */}
      <rect
        x={x + CELL_PADDING}
        y={y + CELL_PADDING}
        width={innerWidth}
        height={innerHeight}
        rx={innerRadius}
        ry={innerRadius}
        style={{
          fill: color,
          fillOpacity: opacity,
          stroke: color,
          strokeOpacity: opacity,
          strokeWidth: isLabelHovered ? 1.5 : 1,
        }}
        className="transition-all duration-100 ease-in-out"
      />

      {/* Content area as HTML inside foreignObject */}
      <foreignObject
        x={x + TEXT_PADDING + CELL_PADDING}
        y={y + TEXT_PADDING + CELL_PADDING}
        width={contentWidth}
        height={contentHeight}
        style={{ pointerEvents: "none" }}
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        {/* Replace this div with any React/HTML you need (badges, etc.) */}
        <div
          className="flex flex-row gap-4 justify-between h-full p-1"
          style={{ fontSize: FONT_SIZE }}
        >
          <span
            className={cn(
              "block overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium leading-none text-foreground",
              !firstWordFitsHorizontally && !verticalFits && "hidden",
              !firstWordFitsHorizontally &&
                verticalFits &&
                "rotate-180 [writing-mode:vertical-rl] [text-orientation:mixed]",
            )}
            title={nameStr}
          >
            {nameStr}
          </span>
          {/* TODO: Add potential solve rate badge when KnowledgeBase suggestions are ready */}
          {/* {contentWidth >= 24 && contentHeight >= 24 && (
            <div className="flex flex-row gap-0.5 items-start shrink-0">
              {(node.isNewTopic ?? node.payload?.isNewTopic) && <Sparkle size={12} />}
              {(node.avgPotentialSolveRate ?? node.payload?.avgPotentialSolveRate) != null &&
                (node.avgPotentialSolveRate ?? node.payload?.avgPotentialSolveRate ?? 0) -
                  (node.avgSolveRate ?? node.payload?.avgSolveRate ?? 0) >=
                  0.2 && <BookKey size={12} />}
            </div>
          )} */}
        </div>
      </foreignObject>
    </g>
  )
}
